// eslint-disable-next-line jira/restricted/react-component-props
import type { ComponentProps, ReactElement } from 'react';
import type { components } from '@atlaskit/select';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';

export type ValueType<T> = {
	id: string;
	name: string;
	data: T;
};

export type Tier = {
	level: number;
};

type Properties = {
	compass_component: {
		id: string;
		url: string;
	};
};

// HeaderContent uses only id and name of the service and mainAggQuery has only name and id of the ser
export type BasicServiceDetails = {
	id: string;
	name: string;
	compassId?: string | null;
};

export type ServiceDetails = BasicServiceDetails & {
	description?: string | null;
	tier: Tier;
	properties?: Properties;
};

export type FieldValue = {
	id: string;
};

export type ServiceEntityFieldConfig = {
	autoCompleteUrl: string;
	value: FieldValue[];
};

export type RestResult = {
	services: ServiceDetails[];
};

export const toValueType = (input: ServiceDetails): ValueType<ServiceDetails> => ({
	id: input.id,
	name: input.name,
	data: input,
});

export type FieldId = string;
export const toFieldId = (id: string): FieldId => id;
export const toString = (fieldId: FieldId): string => fieldId;

export type HeaderRenderProps<T> = {
	isEditing: boolean;
	isExpanded: boolean;
	value: ValueType<T>;
};

export type DetailRenderProps<T> = {
	isEditing: boolean;
	value: ValueType<T>;
};

export type OptionRenderProps<T> = JSX.LibraryManagedAttributes<
	typeof components.Option,
	ComponentProps<typeof components.Option>
> & {
	data: {
		label: string;
		value: string;
		rawData: ValueType<T>;
	};
};

export type AdditionalLabelRenderProps<T> = {
	values: ValueType<T>[];
};

export type HeaderRenderType<T> = (props: HeaderRenderProps<T>) => ReactElement;

export type DetailRenderType<T> = (props: DetailRenderProps<T>) => ReactElement;

export type OptionRenderType<T> = (props: OptionRenderProps<T>) => ReactElement;

export type AdditionalLabelRenderType<T> = (props: AdditionalLabelRenderProps<T>) => ReactElement;

export type OptionsType<T> = {
	value: string;
	label: string;
	rawData: ValueType<T>;
};

export type UseResolveFieldValuesByIdResultType<T> = ValueType<T> | Error;

export type UseResolveFieldValuesByIdType<T> = (
	fieldId: FieldId,
	ids: FieldValue[],
) => {
	isLoading: boolean;
	data: UseResolveFieldValuesByIdResultType<T>[] | undefined;
	error: Error | undefined;
};

export type AutocompleteReturnType<T> = {
	selectProps: {
		isLoading: boolean;
		options: OptionsType<T>[];
		onInputChange: (arg1: string) => void;
		onMenuScrollToBottom?: () => Promise<void>;
		onMenuClose: () => void;
	};
};

export type AutoCompleteConfigType<C, T> = {
	context: C;
	useAutoComplete: (currentValues: ValueType<T>[], context: C) => AutocompleteReturnType<T>;
};

export type RenderType<T> = {
	HeaderContent: HeaderRenderType<T>;
	DetailContent: DetailRenderType<T>;
	OptionContent: OptionRenderType<T>;
	AdditionalLabelContent?: AdditionalLabelRenderType<T>;
};

export type Messages = {
	addItem: MessageDescriptor;
	removeItem: MessageDescriptor;
	editField: MessageDescriptor;
	showDetails: MessageDescriptor;
	hideDetails: MessageDescriptor;
	itemCount: MessageDescriptor;
	searchPlaceholder: MessageDescriptor;
	infoSingleItemMessage: MessageDescriptor;
	warningMultipleItemMessage: MessageDescriptor;
	globalErrorMessage: MessageDescriptor;
};
